import { globalizeSelectors } from '../Utils';
import { consts } from 'js/api/Spectro';
import { selectors as configSelectors } from './Configuration';
import { COLDATACLASS_45 } from '../../Constants';
import type { ConfigurationState } from '../types/Configuration';
import type {
  SpectroAction,
  SpectroConfigState,
  SpectroDeviceButtonUses,
  SpectroDialog,
  SpectroDownloadedMeasurement,
  SpectroDriverPayload,
  SpectroMeasureResult,
  SpectroModePayload,
  SpectroModelInfo,
  SpectroPortPayload,
  SpectroState,
  SpectroStatusState,
} from '../types/Spectro';

type SpectroSelectorState = {
  configurations: ConfigurationState;
  spectro: SpectroState;
};

const actionTypes = {
  FETCHING: 'SPECTRO/FETCHING',

  FETCH_CONFIG: 'SPECTRO/FETCH_CONFIG',
  RECEIVE_CONFIG: 'SPECTRO/RECEIVE_CONFIG',
  RECEIVE_MODEL_INFO: 'SPECTRO/RECEIVE_MODEL_INFO',
  SET_DRIVER: 'SPECTRO/SET_DRIVER',
  SET_PORT: 'SPECTRO/SET_PORT',
  CONFIGURE: 'SPECTRO/CONFIGURE',

  FETCH_STATUS: 'SPECTRO/FETCH_STATUS',
  RECEIVE_STATUS: 'SPECTRO/RECEIVE_STATUS',
  INSTALL: 'SPECTRO/INSTALL',
  RECEIVE_CALIB_RESULT: 'SPECTRO/RECEIVE_CALIB_RESULT',

  CALIBRATE: 'SPECTRO/CALIBRATE',
  CALIB_PROCEED: 'SPECTRO/CALIB_PROCEED',

  SHOW_GLOSS_MODAL: 'SPECTRO/SHOW_GLOSS_MODAL',
  SET_GLOSS: 'SPECTRO/SET_GLOSS',

  MEASURE_TARGET: 'SPECTRO/MEASURE_TARGET',
  MEASURE_PAINT: 'SPECTRO/MEASURE_PAINT',
  RECEIVE_MEASURE_TARGET: 'SPECTRO/RECEIVE_MEASURE_TARGET',
  RECEIVE_MEASURE_PAINT: 'SPECTRO/RECEIVE_MEASURE_PAINT',
  CLEAR_MEASUREMENT: 'SPECTRO/CLEAR_MEASUREMENT',

  FETCH_DOWNLOAD: 'SPECTRO/FETCH_DOWNLOAD',
  RECEIVE_DOWNLOAD: 'SPECTRO/RECEIVE_DOWNLOAD',
  CLEAR_STORED: 'SPECTRO/CLEAR_STORED',

  SET_DIALOG: 'SPECTRO/SET_DIALOG',
  RECEIVE_ERROR: 'SPECTRO/RECEIVE_ERROR',
  CLEAR_ERROR: 'SPECTRO/CLEAR_ERROR',

  SET_MODE: 'SPECTRO/SET_MODE',
  SET_BUSY: 'SPECTRO/SET_BUSY',
} as const;

function payloadAs<Payload>(action: SpectroAction): Payload {
  return action.payload as Payload;
}

const actions = {
  fetching: (): SpectroAction => ({ type: actionTypes.FETCHING }),
  fetchConfig: (): SpectroAction => ({ type: actionTypes.FETCH_CONFIG }),
  receiveConfig: (
    payload: SpectroConfigState
  ): SpectroAction<SpectroConfigState> => ({
    type: actionTypes.RECEIVE_CONFIG,
    payload,
  }),
  receiveModelInfo: (
    payload: SpectroModelInfo | null
  ): SpectroAction<SpectroModelInfo | null> => ({
    type: actionTypes.RECEIVE_MODEL_INFO,
    payload,
  }),
  setDriver: (name: string): SpectroAction<SpectroDriverPayload> => ({
    type: actionTypes.SET_DRIVER,
    payload: { name },
  }),
  setPort: (
    name: string,
    address: string | null = null
  ): SpectroAction<SpectroPortPayload> => ({
    type: actionTypes.SET_PORT,
    payload: { name, address },
  }),
  configure: (): SpectroAction => ({ type: actionTypes.CONFIGURE }),
  fetchStatus: (): SpectroAction => ({ type: actionTypes.FETCH_STATUS }),
  receiveStatus: (
    payload: SpectroStatusState
  ): SpectroAction<SpectroStatusState> => ({
    type: actionTypes.RECEIVE_STATUS,
    payload,
  }),
  install: (): SpectroAction => ({ type: actionTypes.INSTALL }),
  receiveCalibResult: (
    calibrationNeeded: boolean | null
  ): SpectroAction<boolean | null> => ({
    type: actionTypes.RECEIVE_CALIB_RESULT,
    payload: calibrationNeeded,
  }),

  calibrate: (): SpectroAction => ({ type: actionTypes.CALIBRATE }),
  calibProceed: (): SpectroAction => ({ type: actionTypes.CALIB_PROCEED }),

  showGlossModal: (): SpectroAction => ({
    type: actionTypes.SHOW_GLOSS_MODAL,
  }),
  setGloss: (glossValue: number): SpectroAction<number> => ({
    type: actionTypes.SET_GLOSS,
    payload: glossValue,
  }),

  measureTarget: (): SpectroAction => ({ type: actionTypes.MEASURE_TARGET }),
  measurePaint: (): SpectroAction => ({ type: actionTypes.MEASURE_PAINT }),
  receiveMeasureTarget: (
    payload: SpectroMeasureResult
  ): SpectroAction<SpectroMeasureResult> => ({
    type: actionTypes.RECEIVE_MEASURE_TARGET,
    payload,
  }),
  receiveMeasurePaint: (
    payload: SpectroMeasureResult
  ): SpectroAction<SpectroMeasureResult> => ({
    type: actionTypes.RECEIVE_MEASURE_PAINT,
    payload,
  }),
  clearMeasurement: (): SpectroAction => ({
    type: actionTypes.CLEAR_MEASUREMENT,
  }),

  fetchDownload: (): SpectroAction => ({ type: actionTypes.FETCH_DOWNLOAD }),
  receiveDownload: (
    data: SpectroDownloadedMeasurement[] | null | undefined
  ): SpectroAction<SpectroDownloadedMeasurement[]> => ({
    type: actionTypes.RECEIVE_DOWNLOAD,
    payload: data || [],
  }),

  setDialog: (payload: SpectroDialog): SpectroAction<SpectroDialog> => ({
    type: actionTypes.SET_DIALOG,
    payload,
  }),
  receiveError: (payload: unknown): SpectroAction<unknown> => ({
    type: actionTypes.RECEIVE_ERROR,
    payload,
  }),
  clearError: (): SpectroAction => ({ type: actionTypes.CLEAR_ERROR }),
  clearStoredMeasurements: (): SpectroAction => ({
    type: actionTypes.CLEAR_STORED,
  }),

  setMode: (
    aperture: string,
    specularMode: string,
    uvMode: string
  ): SpectroAction<SpectroModePayload> => ({
    type: actionTypes.SET_MODE,
    payload: { aperture, specularMode, uvMode },
  }),

  setBusy: (busy: boolean): SpectroAction<boolean> => ({
    type: actionTypes.SET_BUSY,
    payload: busy,
  }),
};

// reducer

const mountPath = 'spectro'; // mount point in global state, must match root reducer

const initialState: SpectroState = {
  busy: false, // set to true when measuring etc action in progress.
  config: null,
  model_info: null,
  status: null,
  error: null,
  dialog: null,
  measurement_target: null,
  measurement_paint: null,
  downloaded_measurements: [],

  show_gloss_modal: false,
  gloss_value: null,
  gloss_options: [
    { key: 'matt', value: 5, range: [0, 9] },
    { key: 'eggshell', value: 20, range: [10, 25] },
    { key: 'semiGloss', value: 40, range: [26, 40] },
    { key: 'gloss', value: 60, range: [41, 69] },
    { key: 'highGloss', value: 80, range: [70, 89] },
  ],
};

function reducer(
  state: SpectroState = initialState,
  action: SpectroAction
): SpectroState {
  switch (action.type) {
    case actionTypes.FETCHING: {
      return { ...state, busy: true, error: null };
    }
    case actionTypes.SET_BUSY: {
      return { ...state, busy: payloadAs<boolean>(action) };
    }
    case actionTypes.RECEIVE_CONFIG: {
      return {
        ...state,
        busy: false,
        config: payloadAs<SpectroConfigState>(action),
        status: null,
        error: null,
      };
    }
    case actionTypes.RECEIVE_MODEL_INFO: {
      return {
        ...state,
        model_info: payloadAs<SpectroModelInfo | null>(action),
        busy: false,
        error: null,
      };
    }
    case actionTypes.RECEIVE_STATUS: {
      return {
        ...state,
        busy: false,
        status: payloadAs<SpectroStatusState>(action),
        error: null,
      };
    }
    case actionTypes.RECEIVE_CALIB_RESULT: {
      return {
        ...state,
        status: {
          ...state.status,
          calibrationNeeded: payloadAs<boolean | null>(action),
        },
      };
    }
    case actionTypes.SET_DIALOG: {
      return { ...state, dialog: payloadAs<SpectroDialog>(action) };
    }
    case actionTypes.SHOW_GLOSS_MODAL: {
      return { ...state, show_gloss_modal: true, gloss_value: null };
    }
    case actionTypes.SET_GLOSS: {
      return {
        ...state,
        show_gloss_modal: false,
        gloss_value: payloadAs<number>(action),
      };
    }
    case actionTypes.RECEIVE_MEASURE_TARGET: {
      return {
        ...state,
        measurement_target: payloadAs<SpectroMeasureResult>(action),
      };
    }
    case actionTypes.RECEIVE_MEASURE_PAINT: {
      return {
        ...state,
        measurement_paint: payloadAs<SpectroMeasureResult>(action),
      };
    }
    case actionTypes.CLEAR_MEASUREMENT: {
      return {
        ...state,
        measurement_target: null,
        measurement_paint: null,
      };
    }
    case actionTypes.RECEIVE_DOWNLOAD: {
      return {
        ...state,
        downloaded_measurements:
          payloadAs<SpectroDownloadedMeasurement[]>(action),
        busy: false,
      };
    }
    case actionTypes.RECEIVE_ERROR: {
      return {
        ...state,
        busy: false,
        dialog: null,
        error: action.payload,
      };
    }
    case actionTypes.CLEAR_ERROR: {
      return { ...state, error: null };
    }

    default: {
      return state;
    }
  }
}

// selectors

const localSelectors = {
  config: (state: SpectroState): SpectroState['config'] => state.config,
  model_info: (state: SpectroState): SpectroState['model_info'] =>
    state.model_info,
  status: (state: SpectroState): SpectroState['status'] => state.status,
  error: (state: SpectroState): SpectroState['error'] => state.error,
  dialog: (state: SpectroState): SpectroState['dialog'] => state.dialog,
  busy: (state: SpectroState): boolean => state.busy,
  measurement_target: (
    state: SpectroState
  ): SpectroState['measurement_target'] => state.measurement_target,
  measurement_paint: (state: SpectroState): SpectroState['measurement_paint'] =>
    state.measurement_paint,
  downloaded_measurements: (
    state: SpectroState
  ): SpectroState['downloaded_measurements'] => state.downloaded_measurements,
  gloss_options: (state: SpectroState): SpectroState['gloss_options'] =>
    state.gloss_options,
  show_gloss_modal: (state: SpectroState): boolean => state.show_gloss_modal,

  /** True if either calibration or measuring has both options */
  has_button_option: ({ status }: SpectroState): boolean => {
    if (status) {
      const { triggers = [] } = status;
      return (
        (triggers.includes(consts.TRIGGER_CALIB_DEVICE) &&
          triggers.includes(consts.TRIGGER_CALIB_SW)) ||
        (triggers.includes(consts.TRIGGER_MEAS_DEVICE) &&
          triggers.includes(consts.TRIGGER_MEAS_SW))
      );
    }
    return false;
  },
};

const globalized = globalizeSelectors(localSelectors, mountPath);
const selectors = {
  ...globalized,
  use_gloss_compensation: (state: SpectroSelectorState): boolean => {
    // true when option enabled and device requires compensation
    const status = globalized.status(state);
    const { spectro_gloss_compensation } = configSelectors.config_values(state);
    return Boolean(
      status &&
        status.coldataClass === COLDATACLASS_45 &&
        spectro_gloss_compensation
    );
  },
  device_button_uses: (
    state: SpectroSelectorState
  ): SpectroDeviceButtonUses => {
    const status = globalized.status(state);
    if (status) {
      const { triggers = [] } = status;
      const { spectro_read_with_dev_btn } =
        configSelectors.config_values(state);
      return {
        calibrate: spectro_read_with_dev_btn
          ? triggers.includes(consts.TRIGGER_CALIB_DEVICE)
          : !triggers.includes(consts.TRIGGER_CALIB_SW),
        measure: spectro_read_with_dev_btn
          ? triggers.includes(consts.TRIGGER_MEAS_DEVICE)
          : !triggers.includes(consts.TRIGGER_MEAS_SW),
      };
    } else return {};
  },
};

export { actionTypes, actions as default, selectors, reducer };
